import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import { entityService } from "../../services/entityService";

const log = createFileLogger('TOOL_CREATE');

export const toolCreateCommand: SelfContainedCommand = {
  id: 'tool_create',
  description: `Create a new tool that extends River's capabilities.

Tools can be used in documents, command palette, and by AI agents.

**PARAMETERS:**
- id: Unique tool identifier (snake_case, e.g., "my_custom_tool")
- name: Display name for the tool (e.g., "My Custom Tool")
- description: What the tool does
- instructions: AI instructions for how to execute the tool
- icon: Phosphor icon name (e.g., "Wrench", "Star")
- category: Tool category (e.g., "writing", "analysis", "automation")
- placement: Where the tool appears (optional)

**EXAMPLE:**
{
  "id": "analyze_sentiment",
  "name": "Analyze Sentiment",
  "description": "Analyze the emotional tone of text",
  "instructions": "Read the text. Analyze sentiment (positive/negative/neutral). Return analysis.",
  "icon": "SmileyWink",
  "category": "analysis"
}

**RETURNS:** Tool entity
`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Unique tool ID (snake_case)',
        required: true,
        validator: (value: string) => {
          if (!value || typeof value !== 'string') {
            return 'Tool ID is required';
          }
          if (!/^[a-z][a-z0-9_]*$/.test(value)) {
            return 'Tool ID must be snake_case (e.g., my_tool, analyze_text)';
          }
          return null;
        }
      },
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the tool',
        required: true
      },
      {
        name: 'description',
        type: 'string',
        description: 'What the tool does (1-2 sentences)',
        required: true
      },
      {
        name: 'instructions',
        type: 'string',
        description: 'AI instructions for executing this tool',
        required: true
      },
      {
        name: 'icon',
        type: 'string',
        description: 'Phosphor icon name',
        required: false
      },
      {
        name: 'category',
        type: 'string',
        description: 'Tool category (writing, analysis, research, automation)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          id: 'analyze_sentiment',
          name: 'Analyze Sentiment',
          description: 'Analyze the emotional tone of text',
          instructions: 'Read the text. Analyze sentiment (positive/negative/neutral). Return analysis.',
          icon: 'SmileyWink',
          category: 'analysis'
        },
        expectedOutput: createSuccessResult('Tool "Analyze Sentiment" created successfully', {}),
        description: 'Create a sentiment analysis tool',
        context: 'When you need to analyze text emotion'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, name, description, instructions, icon, category, placement } = params;
    
    log.info('[tool_create] Creating tool:', { id, name });
    
    try {
      // Validate required fields
      if (!id || !name || !description || !instructions) {
        return createErrorResult(
          'Missing required fields',
          'id, name, description, and instructions are required',
          'validation',
          { data: params }
        );
      }
      
      // Check if tool already exists
      const existing = await entityService.queryEntities({
        entityType: 'Tool',
        'skeleton.manifest.id': id
      });
      
      if (existing && existing.length > 0) {
        return createErrorResult(
          'Tool already exists',
          `A tool with ID "${id}" already exists`,
          'validation',
          { data: { id, existingId: existing[0]._id } }
        );
      }
      
      // Create Tool entity
      const newTool = await entityService.create({
        entityType: 'Tool',
        name,
        icon: icon || 'Wrench',
        skeleton: {
          '@type': 'Tool',
          manifest: {
            id,
            version: '1.0.0',
            name,
            description,
            instructions,
            author: context.userId || 'user',
            icon: icon || 'Wrench',
            category: category || 'custom',
            isPaid: false,
            price: null,
            pricingType: 'free',
            planRequirement: 'free',
            placement: placement || {
              documentDropdown: true,
              commandPalette: true,
              agenticMode: true,
              pinnableToEditor: true,
              chatSidebar: false
            },
            parameters: [
              {
                name: 'text',
                type: 'string',
                description: 'Input text',
                required: true
              }
            ],
            performance: {
              expectedDuration: 10000,
              complexity: 'medium',
              memoryIntensive: false
            },
            customEntities: [],
            backend: null
          },
          code: null,
          publishing: {
            isPublished: false,
            publishedAt: null,
            isDraft: true,
            isVerified: false,
            isStaffPick: false,
            visibility: 'private'
          },
          stats: {
            installCount: 0,
            purchaseCount: 0,
            rating: null,
            reviewCount: 0
          },
          sandbox: {
            allowNetworkAccess: false,
            allowFileSystemAccess: false,
            allowDatabaseAccess: false,
            maxExecutionTime: 30000,
            maxMemory: 256
          },
          versionHistory: [],
          isSystemTool: false
        }
      });
      
      log.info('[tool_create] Created tool:', newTool._id);
      
      // Reload tool registry
      try {
        const { toolRegistry } = await import('../../tools/registry');
        await toolRegistry.loadAllTools();
        log.info('[tool_create] Tool registry reloaded');
      } catch (reloadError) {
        log.warn('[tool_create] Failed to reload tool registry:', reloadError);
      }
      
      return createSuccessResult(
        `Tool "${name}" created successfully`,
        {
          tool: {
            id: newTool._id || newTool.id,
            toolId: id,
            name,
            description,
            version: '1.0.0'
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityService.create',
            affectedEntities: [newTool._id || newTool.id]
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[tool_create] Error:', error);
      return createErrorResult(
        'Tool creation failed',
        error.message || 'Unknown error',
        'execution',
        {
          data: params,
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
